import type { ReactNode } from "react";

interface Props {
  id?: string;
  /** Small caps accent label above the title ("The problem", "Pricing"). */
  eyebrow?: string;
  title?: string;
  lede?: string;
  children?: ReactNode;
  /** Widens the content column; the default reads like a text page. */
  wide?: boolean;
  /**
   * Heading element for the title. Sub-pages whose first section carries the
   * page title pass "h1" so the page keeps a top-level heading; everywhere
   * else the default "h2" sits under the landing hero's h1.
   */
  headingLevel?: "h1" | "h2";
}

/**
 * One landing section: revealed on scroll, serif sentence title, optional lede.
 *
 * Every section sits in the same 1080 container as the header and the hero, so
 * the page keeps one left edge all the way down; `wide` widens the *content*
 * inside that container rather than the container itself. A narrow section that
 * centred its own box would step its heading inward and read as a different
 * page (which is what the prototype's first screenshot pass showed).
 *
 * The reveal is not managed here: the `_home` layout's `useScrollReveal`
 * observes every `.gs-reveal` under `.gs-site` and stamps `data-shown`
 * directly on the DOM. Owning that attribute from React here would fight it —
 * a re-render would strip the observer's stamp and re-hide the section.
 */
export function Section({
  id,
  eyebrow,
  title,
  lede,
  children,
  wide,
  headingLevel: Heading = "h2",
}: Props) {
  return (
    <section
      id={id}
      className="gs-reveal px-6 mx-auto"
      style={{ maxWidth: 1080, paddingBlock: 56 }}
    >
      <div style={{ maxWidth: wide ? undefined : 720 }}>
        {eyebrow && <p className="gs-eyebrow">{eyebrow}</p>}
        {title && (
          <Heading
            style={{
              fontFamily: "var(--gs-font-display)",
              fontSize: "var(--gs-text-section)",
              fontWeight: 600,
              lineHeight: 1.2,
              marginBottom: lede ? 12 : 28,
            }}
          >
            {title}
          </Heading>
        )}
        {lede && (
          <p
            style={{
              fontSize: "var(--gs-text-lede)",
              lineHeight: 1.6,
              color: "var(--gs-muted)",
              maxWidth: 640,
              marginBottom: 28,
            }}
          >
            {lede}
          </p>
        )}
        {children}
      </div>
    </section>
  );
}
